import { useLayoutEffect } from 'react';

// The bubble's horizontal chrome: richdown-bubble--base px-3.5 (28px) plus its
// borders (0.5px/side normally, 1px/side over background images), with any
// remainder as rounding slack so the measured longest line never re-wraps.
const BUBBLE_HORIZONTAL_CHROME_PX = 30;

// Content with non-paragraph blocks keeps its natural width — code blocks,
// lists, and quotes read better filling the bubble's full cap.
const UNHUGGABLE_BLOCK_SELECTOR = 'pre, ul, ol, blockquote, table';

// The widest rendered line box across the content's paragraphs. Range client
// rects come back per line fragment, so links/mentions/emoji sharing a line are
// each measured from the content's left edge and the max wins.
const measureWidestLineWidth = (contentRoot) => {
  const rootLeft = contentRoot.getBoundingClientRect().left;
  const range = document.createRange();
  let widest = 0;

  for (const paragraph of contentRoot.querySelectorAll('p')) {
    range.selectNodeContents(paragraph);
    for (const rect of range.getClientRects()) {
      widest = Math.max(widest, rect.right - rootLeft);
    }
  }

  return widest;
};

const applyHuggedWidth = (bubbleEl, contentRoot) => {
  bubbleEl.style.maxWidth = '';
  bubbleEl.style.width = '';
  if (contentRoot.querySelector(UNHUGGABLE_BLOCK_SELECTOR)) return;

  // Bubbles wrap greedily (no text-pretty), so the shrink can't re-break the
  // lines: hugging the widest rendered line is exact in a single pass.
  const widestLineWidth = measureWidestLineWidth(contentRoot);
  if (!widestLineWidth) return;

  const huggedWidth = Math.ceil(widestLineWidth) + BUBBLE_HORIZONTAL_CHROME_PX;
  const naturalWidth = bubbleEl.getBoundingClientRect().width;

  if (huggedWidth < naturalWidth) {
    bubbleEl.style.maxWidth = `${huggedWidth}px`;
    return;
  }

  // Whole-pixel snap: a shrink-to-fit box otherwise keeps its fractional text
  // width, which puts everything beside the bubble (the hover reaction button)
  // on half pixels where the circle and glyph rasterize out of agreement.
  // Growing by under 1px can never re-wrap the lines; flex shrink still wins
  // in narrow columns.
  bubbleEl.style.width = `${Math.ceil(naturalWidth)}px`;
};

// Shrink-wraps a message bubble to its longest rendered line. Once text wraps,
// a shrink-to-fit box stays at its max-width instead of re-hugging the wrapped
// lines, leaving dead space after the ragged edge; this measures the real line
// boxes and caps the bubble there. Uses max-width (not width) so bubbles still
// compress naturally inside narrow columns.
const useHuggedBubbleWidth = ({ bubbleRef, contentRef, enabled, contentKey }) => {
  useLayoutEffect(() => {
    const bubbleEl = bubbleRef.current;
    if (!bubbleEl) return undefined;

    if (!enabled) {
      bubbleEl.style.maxWidth = '';
      bubbleEl.style.width = '';
      return undefined;
    }

    const contentRoot = contentRef.current?.querySelector('.richdown-content--base');
    if (!contentRoot) {
      bubbleEl.style.maxWidth = '';
      bubbleEl.style.width = '';
      return undefined;
    }

    applyHuggedWidth(bubbleEl, contentRoot);

    let frame = null;
    const scheduleRemeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => applyHuggedWidth(bubbleEl, contentRoot));
    };

    // Re-hug when the row's available width changes (window or panel resizes).
    const rowEl = bubbleEl.closest('.group\\/row');
    const resizeObserver = rowEl ? new ResizeObserver(scheduleRemeasure) : null;
    resizeObserver?.observe(rowEl);

    // Fallback-font metrics differ; re-hug once webfonts finish loading.
    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (!cancelled) scheduleRemeasure();
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      bubbleEl.style.maxWidth = '';
      bubbleEl.style.width = '';
    };
  }, [bubbleRef, contentRef, enabled, contentKey]);
};

export default useHuggedBubbleWidth;
